import React, { useState, useEffect } from "react";
import socketService from "../services/socket";
import { deviceAPI, nodeAPI, meterAPI } from "../services/api";
import "./MeterDisplay.css";

function MeterDisplay() {
  const [meterData, setMeterData] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [devices, setDevices] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(() => {
    return localStorage.getItem("lastSelectedDeviceId") || "";
  });
  const [nodeReadings, setNodeReadings] = useState({}); // { nodeId: { last: {}, live: {} } }
  const [nodeSearch, setNodeSearch] = useState("");

  // Fetch devices and nodes
  useEffect(() => {
    const fetchDevicesAndNodes = async () => {
      try {
        const devicesRes = await deviceAPI.getAll();
        setDevices(devicesRes.data.data || []);
      } catch {}
    };
    fetchDevicesAndNodes();
  }, []);

  useEffect(() => {
    if (!selectedDeviceId) {
      setNodes([]);
      setNodeReadings({});
      return;
    }
    // Save to localStorage
    localStorage.setItem("lastSelectedDeviceId", selectedDeviceId);
    const fetchNodesAndReadings = async () => {
      try {
        const nodesRes = await nodeAPI.getByDevice(selectedDeviceId);
        const nodesList = nodesRes.data.data || [];
        setNodes(nodesList);
        // Fetch last reading for each node
        const readings = {};
        await Promise.all(
          nodesList.map(async (node) => {
            try {
              const res = await meterAPI.getLatest(
                selectedDeviceId,
                node.nodeId
              );
              readings[node.nodeId] = { last: res.data.data, live: null };
            } catch {
              readings[node.nodeId] = { last: null, live: null };
            }
          })
        );
        setNodeReadings(readings);
      } catch {
        setNodes([]);
        setNodeReadings({});
      }
    };
    fetchNodesAndReadings();
  }, [selectedDeviceId]);

  // Socket connection for live data for all nodes of selected device
  useEffect(() => {
    socketService.connect();
    setConnectionStatus("connected");

    const handleMeterData = (data) => {
      if (data.deviceId === selectedDeviceId) {
        setNodeReadings((prev) => ({
          ...prev,
          [data.nodeId]: {
            ...(prev[data.nodeId] || {}),
            live: data,
          },
        }));
      }
      setMeterData((prevData) => {
        const existingIndex = prevData.findIndex(
          (item) =>
            item.deviceId === data.deviceId && item.nodeId === data.nodeId
        );
        if (existingIndex !== -1) {
          const newData = [...prevData];
          newData[existingIndex] = data;
          return newData;
        } else {
          return [...prevData, data];
        }
      });
    };
    socketService.onMeterData(handleMeterData);

    return () => {
      socketService.offMeterData();
      socketService.disconnect();
      setConnectionStatus("disconnected");
    };
  }, [selectedDeviceId]);

  const getStatusColor = (value, type) => {
    switch (type) {
      case "voltage":
        if (value >= 220 && value <= 240) return "status-good";
        if (value >= 200 && value < 220) return "status-warning";
        return "status-danger";
      case "powerFactor":
        if (value >= 0.9) return "status-good";
        if (value >= 0.8) return "status-warning";
        return "status-danger";
      default:
        return "status-good";
    }
  };

  return (
    <div className="meter-display">
      <div className="header">
        <h2>Real-Time Meter Data</h2>
        {/* <div className={`connection-status ${connectionStatus}`}>
          Socket Status:
          <span className="status-dot"></span>
          {connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
        </div> */}


        <div
          style={{
            marginBottom: 24,
            display: "flex",
            gap: 16,
            alignItems: "center",
          }}
        >
          <label style={{ fontWeight: 600 }}>Device:</label>
          <select
            value={selectedDeviceId}
            onChange={(e) => {
              setSelectedDeviceId(e.target.value);
            }}
            style={{ padding: 8, borderRadius: 6 }}
          >
            <option value="">Select Device</option>
            {devices.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.deviceName} ({device.deviceId})
              </option>
            ))}
          </select>
        </div>



        <div
          style={{
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <input
            type="text"
            placeholder="Search node by ID or name..."
            value={nodeSearch}
            onChange={(e) => setNodeSearch(e.target.value)}
            style={{
              padding: 8,
              borderRadius: 6,
              width: 260,
              border: "1px solid #ccc",
            }}
          />
        </div>

        
      </div>

      {/* Device Selector */}

      {/* Show all nodes for selected device */}
      {!selectedDeviceId ? (
        <div className="no-data">
          <p>Please select a device to view meter data.</p>
        </div>
      ) : nodes.length === 0 ? (
        <div className="no-data">
          <p>No nodes found for this device.</p>
        </div>
      ) : (
        <>
          {/* Node search bar */}

          <div className="meter-grid">
            {nodes
              .filter(
                (node) =>
                  node.nodeId
                    .toLowerCase()
                    .includes(nodeSearch.toLowerCase()) ||
                  (node.nodeName || "")
                    .toLowerCase()
                    .includes(nodeSearch.toLowerCase())
              )
              .map((node) => {
                const reading =
                  nodeReadings[node.nodeId]?.live ||
                  nodeReadings[node.nodeId]?.last;
                return (
                  <div key={node.nodeId} className="meter-card">
                    <div className="meter-header">
                      <h3>Node: {node.nodeId}</h3>
                      <span className="device-badge">
                        Device: {selectedDeviceId}
                      </span>
                      <span
                        style={{
                          marginLeft: 16,
                          fontWeight: 600,
                          color: nodeReadings[node.nodeId]?.live
                            ? "#27ae60"
                            : "#2980b9",
                        }}
                      >
                        {nodeReadings[node.nodeId]?.live
                          ? "LIVE"
                          : "Last Reading"}
                      </span>
                    </div>
                    {reading ? (
                      <>
                        {/* Meter values at top */}
                        <div className="meter-readings modern">
                          <div className="reading-item modern">
                            <div className="reading-label modern">
                              <span className="icon">⚡</span>Current
                            </div>
                            <div className="reading-value modern">
                              {reading.current?.toFixed(2)}{" "}
                              <span className="unit">A</span>
                            </div>
                          </div>
                          <div
                            className={`reading-item modern ${getStatusColor(
                              reading.voltage,
                              "voltage"
                            )}`}
                          >
                            <div className="reading-label modern">
                              <span className="icon">🔌</span>Voltage
                            </div>
                            <div className="reading-value modern">
                              {reading.voltage?.toFixed(2)}{" "}
                              <span className="unit">V</span>
                            </div>
                          </div>
                          <div
                            className={`reading-item modern ${getStatusColor(
                              reading.powerFactor,
                              "powerFactor"
                            )}`}
                          >
                            <div className="reading-label modern">
                              <span className="icon">📊</span>Power Factor
                            </div>
                            <div className="reading-value modern">
                              {reading.powerFactor?.toFixed(3)}
                            </div>
                          </div>
                          <div className="reading-item modern">
                            <div className="reading-label modern">
                              <span className="icon">⚙️</span>Apparent Power
                            </div>
                            <div className="reading-value modern">
                              {reading.apparentPower?.toFixed(2)}{" "}
                              <span className="unit">kVA</span>
                            </div>
                          </div>
                        </div>
                        {/* Wi-SUN extra fields in a horizontal scrollable row */}
                        <div className="wisun-extra-row">
                          {/* {reading.chip && <div className="extra-pill"><span className="extra-label">Chip:</span> <span className="extra-value">{reading.chip}</span></div>} */}
                          {reading.parent && (
                            <div className="extra-pill">
                              <span className="extra-label">Parent:</span>{" "}
                              <span className="extra-value">
                                {reading.parent}
                              </span>
                            </div>
                          )}
                          {reading.running && (
                            <div className="extra-pill">
                              <span className="extra-label">Running:</span>{" "}
                              <span className="extra-value">
                                {reading.running}
                              </span>
                            </div>
                          )}
                          {reading.connected && (
                            <div className="extra-pill">
                              <span className="extra-label">Connected:</span>{" "}
                              <span className="extra-value">
                                {reading.connected}
                              </span>
                            </div>
                          )}
                          {reading.disconnected && (
                            <div className="extra-pill">
                              <span className="extra-label">Disconnected:</span>{" "}
                              <span className="extra-value">
                                {reading.disconnected}
                              </span>
                            </div>
                          )}
                          {reading.connections && (
                            <div className="extra-pill">
                              <span className="extra-label">Connections:</span>{" "}
                              <span className="extra-value">
                                {reading.connections}
                              </span>
                            </div>
                          )}
                          {reading.availability && (
                            <div className="extra-pill">
                              <span className="extra-label">Availability:</span>{" "}
                              <span className="extra-value">
                                {reading.availability}
                              </span>
                            </div>
                          )}
                          {reading.connected_total && (
                            <div className="extra-pill">
                              <span className="extra-label">
                                Connected Total:
                              </span>{" "}
                              <span className="extra-value">
                                {reading.connected_total}
                              </span>
                            </div>
                          )}
                          {reading.disconnected_total && (
                            <div className="extra-pill">
                              <span className="extra-label">
                                Disconnected Total:
                              </span>{" "}
                              <span className="extra-value">
                                {reading.disconnected_total}
                              </span>
                            </div>
                          )}
                          {/* {reading.Wisun_Data && <div className="extra-pill"><span className="extra-label">Wisun Data:</span> <span className="extra-value">{reading.Wisun_Data}</span></div>} */}
                          {reading.neighbor_info && (
                            <div className="extra-pill">
                              <span className="extra-label">
                                Neighbor Info:
                              </span>
                              <span className="extra-value">
                                {Object.entries(reading.neighbor_info)
                                  .map(([k, v]) => `${k}: ${v}`)
                                  .join(", ")}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="meter-footer modern">
                          <span className="timestamp modern">
                            Last Update:{" "}
                            {reading.timestamp
                              ? new Date(reading.timestamp).toLocaleString()
                              : ""}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div
                        className="no-data modern"
                        style={{
                          background: "transparent",
                          boxShadow: "none",
                          padding: 0,
                        }}
                      >
                        <p>No reading found for this node.</p>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
}

export default MeterDisplay;
